import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AlertTitle from "@mui/material/AlertTitle";
import { nanoid } from "nanoid";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import { documentBuilderService } from "../../services/documentBuilderService";
import Loader from "../../components/Loader";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material";
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { MultiStepFormData, StepDefinition } from "./BusinessInfoTypes";
import { StepCompanyDetails } from "./TechCompanyDetails";
import { StepProjects } from "./StepProjects";
import { StepTestimonials } from "./StepTestimonial";
import { StepPortalInfo } from "./StepPortalInfo";
import { StepIntegrations } from "./StepIntegrations";

const steps: StepDefinition[] = [
  { label: "Company Details", ContentComponent: StepCompanyDetails },
  { label: "Past Projects", ContentComponent: StepProjects },
  { label: "Testimonials", ContentComponent: StepTestimonials },
  { label: "CoreSupport Portal", ContentComponent: StepPortalInfo },
  { label: "Integrations & Finish", ContentComponent: StepIntegrations },
];

const TechSupport: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<Record<number, boolean>>({});
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const { mode, toggleMode } = useThemeMode();
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
  });

  const [formData, setFormData] = React.useState<MultiStepFormData>({
    companyName: "",
    companyDescription: "",
    website: "",
    address: "",
    growthPlan: "",
    logo: [],
    projects: [{ id: nanoid(), name: "", description: "", files: [] }],
    testimonials: [{ id: nanoid(), clientName: "", file: [] }],
    coreSupportInfo: "",
    generalDocuments: [],
    acceptTerms: false,
  });
  const [initialFormData, setInitialFormData] =
    React.useState<MultiStepFormData | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [isDirty, setIsDirty] = React.useState(false);
  React.useEffect(() => {
    fetchBusinessInfo();
  }, []);

  React.useEffect(() => {
    if (!initialFormData) return;
    const dirty = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setIsDirty(dirty);
  }, [formData, initialFormData]);
  const fetchBusinessInfo = async () => {
    setLoading(true); // start loader
    try {
      const files =
        await documentBuilderService.listDocuments("/Business_info");

      if (!files || files.length === 0) return;

      const jsonFile = files.find(
        (file: any) =>
          file.type === "application/json" &&
          file.title === "Business_infofolder.json",
      );

      if (!jsonFile) return;

      const fileUrl = jsonFile.s3_url || jsonFile.url;
      if (!fileUrl) return;

      const { download_url } =
        await documentBuilderService.downloadDocument(fileUrl);
      if (!download_url) return;

      const response = await fetch(download_url);
      if (!response.ok) throw new Error("Failed to fetch file");

      const jsonData = await response.json();

      setFormData({
        companyName: jsonData.companyName ?? "",
        companyDescription: jsonData.companyDescription ?? "",
        website: jsonData.website ?? "",
        address: jsonData.address ?? "",
        growthPlan: jsonData.growthPlan ?? "",
         logo: Array.isArray(jsonData.logo) ? jsonData.logo : [],
        projects:
          jsonData.projects?.length > 0
            ? jsonData.projects
            : [{ id: nanoid(), name: "", description: "", files: [] }],
        testimonials:
          jsonData.testimonials?.length > 0
            ? jsonData.testimonials.map((t: any) => ({
                id: t.id ?? nanoid(),
                clientName: t.clientName ?? "",
                file: t.file?.map((f: any) => f) ?? [],
              }))
            : [{ id: nanoid(), clientName: "", file: [] }],
        coreSupportInfo: jsonData.coreSupportInfo ?? "",
        generalDocuments: jsonData.generalDocuments ?? [],
        acceptTerms: jsonData.acceptTerms ?? false,
      });

      setInitialFormData(JSON.parse(JSON.stringify(jsonData)));
    } catch (error) {
      console.error("Failed to load Business Info:", error);
    } finally {
      setLoading(false); // stop loader
    }
  };

  const totalSteps = steps.length;
  const isLastStep = activeStep === totalSteps - 1;
  const allStepsCompleted = Object.keys(completed).length === totalSteps;

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  const handleUpdate = async () => {
    if (!initialFormData) return;

    setIsUpdating(true); // start loading

    try {
      // Delete previous file
      const files =
        await documentBuilderService.listDocuments("/Business_info");
      const jsonFile = files.find(
        (f: any) => f.title === "Business_infofolder.json",
      );
      if (jsonFile) {
        await documentBuilderService.deleteDocument(jsonFile.id);
      }

      // Upload new data
      const payload = { ...formData };
      const jsonBlob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const newFile = new File([jsonBlob], "Business_infofolder.json", {
        type: "application/json",
      });

      await documentBuilderService.uploadDocuments([newFile], "/Business_info");

      // Update initialFormData and reset dirty flag
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
      setIsDirty(false);
      setSnackbar({
        open: true,
        message: "Business Info updated successfully!",
      });
    } catch (error) {
      console.error("Update failed", error);
      setSnackbar({ open: true, message: "Update failed" });
    } finally {
      setIsUpdating(false); // stop loading
    }
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    //  Step 1: Create clean JSON payload
    const payload = {
      companyName: formData.companyName,
      companyDescription: formData.companyDescription,
        logo: formData.logo,
      website: formData.website,
      address: formData.address,
      growthPlan: formData.growthPlan,
      projects: formData.projects.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        files: p.files, //  already string[]
      })),

      testimonials: formData.testimonials.map((t) => ({
        id: t.id,
        clientName: t.clientName,
        files: t.file, //  fix
      })),

      coreSupportInfo: formData.coreSupportInfo,

      generalDocuments: formData.generalDocuments, // fix

      acceptTerms: formData.acceptTerms,
    };

    //  Step 2: Convert JSON → File
    const jsonBlob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });

    const jsonFile = new File([jsonBlob], "Business_infofolder.json", {
      type: "application/json",
    });

    try {
      //  Step 3: Call your upload API
      await documentBuilderService.uploadDocuments(
        [jsonFile], // file array
        "/Business_info", // folder path
      );

      console.log("Uploaded JSON:", payload);

      setFormSubmitted(true);
      setCompleted(steps.reduce((acc, _, i) => ({ ...acc, [i]: true }), {}));
    } catch (error) {
      console.error("Upload failed:", error);
      setSnackbar({
        open: true,
        message: "Upload failed",
      });
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
    setFormSubmitted(false);
  };

  const CurrentStepContent = steps[activeStep].ContentComponent;
  const validateStep = () => {
    if (activeStep === 0) {
      const { companyName, companyDescription, website, address, growthPlan } =
        formData;

      if (
        !companyName ||
        !companyDescription ||
        !website ||
        !address ||
        !growthPlan
      ) {
        return false;
      }
    }

    return true;
  };
  const handleNext = () => {
    const isValid = validateStep();

    if (!isValid) {
      setSnackbar({
        open: true,
        message: "Please fill required fields",
      });
      return;
    }

    setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    setActiveStep((prev) => prev + 1);
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",

        backgroundColor: theme.palette.background.default,
        position: "relative",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={() => navigate("/chat")} size="small">
                <ArrowBackIcon />
              </IconButton>
              <img
                src="/atlasai-icon.svg"
                alt="CoreSupport"
                style={{ width: 32, height: 32 }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    lineHeight: 1.2,
                  }}
                >
                  Core Support
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  Business Info
                </Typography>
              </Box>
            </Box>
            <Tooltip
              title={
                mode === "dark" ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              <IconButton onClick={toggleMode} color="primary">
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Create, edit, and format multi-page documents with structured
            layouts.
          </Typography>
        </Box>

        <div>
          <Box
            sx={{
              position: "relative",
              maxHeight: 940,
              backgroundColor:
                mode === "dark"
                  ? "rgba(15, 23, 42, 0.8)"
                  : "rgba(255, 255, 255, 0.95)",
              mt: 4,
              p: 4,
              borderRadius: "20px",
              zIndex: 1,
              width: {
                xs: "100%",
                sm: "100%",
                md: "80%",
              },
              mx: "auto",

              "&::before": {
                content: '""',
                position: "absolute",
                inset: "-2px",
                borderRadius: "inherit",
                background:
                  "linear-gradient(135deg, #6366f1, #ec4899, #8b5cf6)",
                filter: "blur(14px)",
                opacity: 0.6,
                zIndex: -1,
              },

              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                background:
                  mode === "dark"
                    ? "rgba(15, 23, 42, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                zIndex: -1,
              },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "start",
                mb: 3,
                p: mode === "dark" ? 3 : 0,
                borderRadius: mode === "dark" ? 4 : 0,
                background:
                  mode === "dark"
                    ? "linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1))"
                    : "transparent",
                backdropFilter: mode === "dark" ? "blur(20px)" : "none",
                border:
                  mode === "dark"
                    ? "1px solid rgba(99, 102, 241, 0.3)"
                    : "none",
                boxShadow:
                  mode === "dark"
                    ? "0 8px 32px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    : "none",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  alignSelf: "start",
                }}
              >
                <Box
                  component="img"
                  src="/atlasai-icon.svg"
                  alt="CoreSupport"
                  sx={{
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                  }}
                />
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    fontFamily:
                      "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif",
                    color: mode === "dark" ? "#ffffff" : "#0f172a",
                    background:
                      "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                  }}
                >
                  CoreSupport
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: "100%",
                overflowX: {
                  xs: "auto",
                  sm: "auto",
                  md: "auto",
                  lg: "hidden",
                },
                overflowY: "hidden",

                "&::-webkit-scrollbar": {
                  height: "5px",
                },

                "&::-webkit-scrollbar-thumb": {
                  borderRadius: "10px",
                },
              }}
            >
              <Stepper
                nonLinear
                activeStep={activeStep}
                sx={{
                  display: {
                    xs: "inline-flex", // enable scroll layout
                    lg: "flex", // normal layout
                  },
                  minWidth: {
                    xs: "max-content", // allow overflow
                    lg: "100%", // full width
                  },
                  py: 0.5, 
                  alignItems: "center", 
                }}
              >
                {steps.map((step, index) => (
                  <Step key={step.label} completed={completed[index]}>
                    <StepButton
                      onClick={handleStep(index)}
                      sx={{
                        minWidth: 120,
                      }}
                    >
                      <span style={{ whiteSpace: "nowrap" }}>{step.label}</span>
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            </Box>
            {loading ? ( // show loader while fetching
              <Loader />
            ) : (
              <Box className=" p-4 mt-4 border rounded-lg">
                {formSubmitted && allStepsCompleted ? (
                  <>
                    <Alert severity="success" sx={{ mt: 4, mb: 4 }}>
                      <AlertTitle>Onboarding Complete</AlertTitle>
                      <Typography>
                        Your business profile has been submitted.
                      </Typography>
                    </Alert>

                    <Box display="flex" justifyContent="flex-end">
                      <Button variant="contained" onClick={handleReset}>
                        Start New Registration
                      </Button>
                    </Box>
                  </>
                ) : (
                  <form onSubmit={isLastStep ? handleSubmit : undefined}>
                    <CurrentStepContent
                      formData={formData}
                      setFormData={setFormData}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        pt: 4,
                        flexDirection: {
                          xs: "column",
                          sm: "row",
                        },
                        gap: {
                          xs: 1,
                          sm: 0,
                        },
                        alignItems: {
                          xs: "stretch",
                          sm: "center",
                        },
                      }}
                    >
                      <Button disabled={activeStep === 0} onClick={handleBack}>
                        Back
                      </Button>

                      <Box flex="1 1 auto" />

                      {isLastStep ? (
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={!formData.acceptTerms}
                        >
                          Complete Onboarding
                        </Button>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: {
                              xs: "column",
                              sm: "row",
                            },
                            gap: 1,
                          }}
                        >
                          {initialFormData && (
                            <Button
                              variant="outlined"
                              onClick={handleUpdate}
                              disabled={!isDirty || isUpdating}
                              sx={{ mr: 2 }}
                            >
                              {isUpdating ? "Updating..." : "Update"}
                            </Button>
                          )}

                          <Button variant="contained" onClick={handleNext}>
                            Next
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </form>
                )}
              </Box>
            )}
          </Box>
        </div>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="error" // change this
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechSupport;
